import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { usePermission } from '@/permissions/usePermission';
import { marginControlApi } from '@/api/services/marginControl';
import { ApplyTab } from './tabs/ApplyTab';
import { ConfigurationsTab } from './tabs/ConfigurationsTab';
import { HistoryTab } from './tabs/HistoryTab';

export function MarginControlPage() {
  const can = usePermission();
  const qc = useQueryClient();

  const canFetchPreview = can('User.MarginControl.FetchPreview');
  const canViewConfigs = can('User.MarginControl.ViewConfigurations');
  const canHistory = can('User.MarginControl.HistoryTab');
  const canApply = can('User.MarginControl.Apply');
  const canUndo = can('User.MarginControl.Undo');

  const pageState = useQuery({ queryKey: ['margin-control', 'page-state'], queryFn: marginControlApi.getPageState });
  const options = useQuery({ queryKey: ['margin-control', 'options'], queryFn: marginControlApi.getOptions });

  const tabs = useMemo<TabItem[]>(() => {
    const t: TabItem[] = [];
    if (canFetchPreview) t.push({ key: 'apply', title: 'Apply Configuration' });
    if (canViewConfigs) t.push({ key: 'configurations', title: 'Configurations' });
    if (canHistory) t.push({ key: 'history', title: 'History' });
    return t;
  }, [canFetchPreview, canViewConfigs, canHistory]);

  const [active, setActive] = useState<string>('');
  const activeKey = active || tabs[0]?.key || '';

  const onCrossReload = () => {
    void qc.invalidateQueries({ queryKey: ['margin-control', 'configurations'] });
    void qc.invalidateQueries({ queryKey: ['margin-control', 'history'] });
  };

  if (pageState.isLoading || options.isLoading) return <Spinner />;

  const applyEnabled = pageState.data?.applyEnabled ?? false;
  const applyDisabledReason = pageState.data?.applyDisabledReason ?? '';
  const opts = options.data ?? { modes: [], applyTypes: [], statuses: [] };

  return (
    <div className="margin-control-page">
      <PageHeader title="Margin Control" breadcrumb={['Home', 'Margin Control']} />

      <div className="card custom-card mb-0">
        <div className="card-header p-0">
          <Tabs tabs={tabs} active={activeKey} onChange={setActive} />
        </div>
        <div className="card-body">
          {activeKey === 'apply' && canFetchPreview && (
            <ApplyTab options={opts} applyEnabled={applyEnabled} applyDisabledReason={applyDisabledReason} canApply={canApply} onCrossReload={onCrossReload} />
          )}
          {activeKey === 'configurations' && canViewConfigs && (
            <ConfigurationsTab options={opts} applyEnabled={applyEnabled} applyDisabledReason={applyDisabledReason} canApply={canApply} canUndo={canUndo} onCrossReload={onCrossReload} />
          )}
          {activeKey === 'history' && canHistory && <HistoryTab />}
          {tabs.length === 0 && <p className="text-muted mb-0">You don't have access to any Margin Control tabs.</p>}
        </div>
      </div>
    </div>
  );
}
